import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "@shared/shared/tickerFilterInput";
import { buildShareAvgCostSnapshots, type ShareAvgCostSnapshot } from "@shared/ui/analytics";
import { scopeAllowsAccount, subscribeAccountScope } from "@shared/ui/accountScope";
import { fetchCallsPlusTransactions } from "@shared/ui/callsPlus";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  ledgerRowMark,
  subscribeLedgerMarks,
} from "@shared/ui/ledgerMarks";
import {
  historyActionLabel,
  historyRowOrigin,
  renderHistoryActionPill,
  historySystemLabel,
  historyAccountLabel,
  historyPendingSettlementDisplay,
  type HistoryLedgerMetaTxn,
} from "@shared/ui/historyLabels";
import { loadTransactionsSnapshot, invalidateTransactionsSnapshot, type LedgerSource } from "@shared/ui/wheelTransactions";
import {
  buildOptionSymbolIndex,
  buildStockLegAvgCostDetailIndex,
  compareHistoryRowsForDisplay,
  findHistoryDetailRows,
  formatStockHoldingAvgChange,
  isImpliedStockLeg,
  matchesHistorySideFilter,
  historyInstrumentKind,
  prepareHistoryTransactions,
  prepareOwnLedgerHistoryTransactions,
  type HistoryDetailKind,
  type StockLegAvgCostDetail,
} from "@shared/ui/historyRelated";
import {
  compareOwnLedgerHistoryDisplayRows,
  historyTransactionNotes,
  buildCashBalanceSnapshots,
  isCashFlowHistoryRow,
  isDeletedCashFlowRow,
  type CashBalanceSnapshot,
  loadOwnLedgerHistoryDisplayRows,
  matchesHistoryTickerFilter,
  matchesHistoryTypeFilterExtended,
} from "@shared/ui/historyCashFlows";
import { openHistoryCashFlowEditModalFromTxn } from "@shared/ui/historyCashFlowEdit";
import {
  loadOwnLedgerHealth,
  loadOwnLedgerHistoryBundle,
  setOwnLedgerCashFlowDeleted,
} from "@shared/ui/ownLedgerClient";
import { withPreservedInputFocus } from "@shared/ui/preserveInputFocus";
import {
  HISTORY_TXN_MENU_EVENT,
  renderFocusHistoryRowMenu,
  syncFocusRowMenuAfterDomUpdate,
  type HistoryTxnMenuAction,
} from "@shared/ui/focusActions";
import { confirmDialog } from "@shared/ui/confirmDialog";
import {
  historyTransactionAddDefaultsFromTxn,
  openHistoryTransactionAddModal,
} from "@shared/ui/historyTransactionAdd";
import { openHistoryTransactionEditModal } from "@shared/ui/historyTransactionEdit";
import { promptHistoryAccountReassign } from "@shared/ui/historyAccountReassign";
import { openWheelPairLinkModal } from "@shared/ui/historyWheelPairLink";
import { showToast } from "@shared/ui/toast";
import { WHEEL_LEDGER_CHANGED_EVENT } from "@shared/ui/pageBridge";
import { ensureTxnNoteClickBinding } from "@shared/ui/txnNote";
import {
  deleteLedgerTransaction,
  WheelActionError,
} from "@shared/ui/wheelActions";
import {
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  loadTableColumns,
  renderTableColgroup,
  renderTableHeadRow,
  renderTablePinnedAttr,
  tableColumnTitle,
  type ColumnState,
  visibleTableColumns,
} from "@shared/ui/tableColumns";
import {
  historyMonthChipClassName,
  historyMonthKeyForTxn,
  historyMonthRowYear,
  historyMonthShortLabel,
  historyYearChipClassName,
  monthKeysForYear,
  monthsWithTransactions,
  resolveDefaultHistoryMonth,
} from "@shared/ui/historyDateFilters";
import { wheelGet } from "./api";
import { paintPanelLoadError } from "./authUi";

const FILTERS_KEYS: Record<LedgerSource, string> = {
  stockforge: "sfe-web-history-filters-v1",
  own_ledger: "sfe-web-history-local-filters-v1",
};
const TABLE_ID = "web-history" as const;
const TABLE_ID_LOCAL = "web-history-local" as const;
const CACHE_MS = 3 * 60_000;

type HistoryTxn = Awaited<ReturnType<typeof fetchCallsPlusTransactions>>[number];

export interface HistoryFilterState {
  type: "all" | "option" | "stock" | "cash";
  side: "all" | "put" | "call";
  ticker: string;
  /** Ledger action code from the Action column (STO / ASGN / DEP / …), or "all". */
  action: string;
  date: "all" | "7d" | "30d" | number;
  /** YYYY-MM when filtering a specific month within a selected year. */
  month: string | null;
}

/** Preferred order for the Action filter; codes outside this list follow, sorted. */
const HISTORY_ACTION_CODE_ORDER = [
  "STO",
  "BTC",
  "EXP",
  "ASGN",
  "BUY",
  "SELL",
  "STC",
  "BTO",
  "XFER",
  "DEP",
  "WDL",
  "TAX",
  "MARG",
  "LEND",
  "XFER+",
  "XFER-",
  "ADJ+",
  "ADJ-",
  "IN",
  "OUT",
];

/** Action codes present in the ledger, so the dropdown never offers dead options.
 * The saved code is kept even when absent, otherwise a refresh would silently
 * widen the filter. */
function historyActionCodes(transactions: HistoryTxn[], selected: string): string[] {
  const present = new Set<string>();
  for (const txn of transactions) {
    const code = historyActionLabel(txn);
    if (code && code !== "—") present.add(code);
  }
  if (selected && selected !== "all") present.add(selected);
  const ordered = HISTORY_ACTION_CODE_ORDER.filter((code) => present.has(code));
  const extra = [...present].filter((code) => !HISTORY_ACTION_CODE_ORDER.includes(code)).sort();
  return [...ordered, ...extra];
}

interface HistoryPanelCache {
  transactions: HistoryTxn[];
  years: number[];
  fetchedAt: number;
}

let panelCache: HistoryPanelCache | null = null;
let panelCacheLocal: HistoryPanelCache | null = null;
/** Latest painted cache/state for soft rerenders (filter handlers must not close over a stale cache). */
let liveHistoryCache: HistoryPanelCache | null = null;
let liveHistoryFilterState: HistoryFilterState | null = null;
let liveHistoryLedgerSource: LedgerSource = "stockforge";
let panelInflight: Promise<HistoryPanelCache> | null = null;
let panelInflightLocal: Promise<HistoryPanelCache> | null = null;
let mountedPanel: HTMLElement | null = null;
let repaintHistoryMarks: (() => void) | null = null;
subscribeLedgerMarks(() => {
  repaintHistoryMarks?.();
});
let mountedLedgerSource: LedgerSource = "stockforge";
let historyPanelRerender: (() => void) | null = null;
const historyRerenders = new Map<LedgerSource, () => void>();
subscribeAccountScope(() => {
  for (const rerender of historyRerenders.values()) rerender();
});
let renderGeneration = 0;
const expandedLinkedTxnIds = new Set<number>();
let historyMenuBound = false;
let historyCashEditBound = false;
let historyTxnNotesBound = false;

function filtersKey(source: LedgerSource): string {
  return FILTERS_KEYS[source];
}

/** One filter-state object per ledger, refreshed in place. The bound filter
 *  listeners outlive individual renders, so the identity has to stay stable. */
const liveFilterStates: Partial<Record<LedgerSource, HistoryFilterState>> = {};

function canonicalFilterState(
  state: HistoryFilterState,
  ledgerSource: LedgerSource,
): HistoryFilterState {
  const existing = liveFilterStates[ledgerSource];
  if (!existing) {
    liveFilterStates[ledgerSource] = state;
    return state;
  }
  return Object.assign(existing, state);
}

function activeHistoryCache(): HistoryPanelCache | null {
  return mountedLedgerSource === "own_ledger" ? panelCacheLocal : panelCache;
}

function buildTxnById(transactions: HistoryTxn[]): Map<number, HistoryTxn> {
  const byId = new Map<number, HistoryTxn>();
  for (const txn of transactions) {
    const id = Number(txn.id);
    if (id) byId.set(id, txn);
  }
  return byId;
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function defaultFilters(): HistoryFilterState {
  return {
    type: "all",
    side: "all",
    ticker: "",
    action: "all",
    date: "all",
    month: null,
  };
}

function parseSavedMonth(value: unknown): string | null {
  return typeof value === "string" && /^\d{4}-\d{2}$/.test(value) ? value : null;
}

function loadFilters(source: LedgerSource = mountedLedgerSource): HistoryFilterState {
  try {
    const raw = localStorage.getItem(filtersKey(source));
    if (!raw) return defaultFilters();
    const saved = JSON.parse(raw) as Partial<HistoryFilterState>;
    const date = saved.date;
    let parsedDate: HistoryFilterState["date"] =
      date === "7d" || date === "30d" || date === "all"
        ? date
        : typeof date === "number" && Number.isFinite(date)
          ? date
          : "all";
    let month = parseSavedMonth(saved.month);
    if (month && typeof parsedDate === "number" && !month.startsWith(`${parsedDate}-`)) {
      month = null;
    }
    if (parsedDate === "all" || parsedDate === "7d" || parsedDate === "30d") {
      month = null;
    }
    return {
      type:
        saved.type === "option" || saved.type === "stock" || saved.type === "cash"
          ? saved.type
          : "all",
      side: saved.side === "put" || saved.side === "call" ? saved.side : "all",
      ticker: sanitizeTickerFilterValue(typeof saved.ticker === "string" ? saved.ticker : "").toUpperCase(),
      action:
        typeof saved.action === "string" && /^[A-Z+-]{2,6}$/.test(saved.action)
          ? saved.action
          : "all",
      date: parsedDate,
      month,
    };
  } catch {
    return defaultFilters();
  }
}

function saveFilters(state: HistoryFilterState, source: LedgerSource = mountedLedgerSource): void {
  localStorage.setItem(filtersKey(source), JSON.stringify(state));
}

function countCachedCashRows(transactions: HistoryTxn[]): number {
  return transactions.filter((txn) => isCashFlowHistoryRow(txn)).length;
}

/** Reload when server has cash rows but the in-memory panel cache predates cash import. */
async function ownLedgerCashCacheStale(cached: HistoryPanelCache | null): Promise<boolean> {
  if (!cached) return false;
  if (countCachedCashRows(cached.transactions) > 0) return false;
  try {
    const health = await loadOwnLedgerHealth(true);
    return health.cashFlowCount > 0;
  } catch {
    return false;
  }
}

function extractYears(transactions: HistoryTxn[]): number[] {
  const years = new Set<number>();
  for (const txn of transactions) {
    const year = Number(String(txn.trade_date ?? "").slice(0, 4));
    if (Number.isFinite(year) && year > 1900) years.add(year);
  }
  return [...years].sort((a, b) => b - a);
}

function parseISODate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return d;
}


function matchesDateFilter(txn: HistoryTxn, state: HistoryFilterState): boolean {
  const filter = state.date;
  if (filter === "all") return true;
  const trade = parseISODate(String(txn.trade_date ?? ""));
  if (!trade) return false;
  if (filter === "7d") return trade >= daysAgo(7);
  if (filter === "30d") return trade >= daysAgo(30);
  if (typeof filter === "number") {
    if (trade.getFullYear() !== filter) return false;
    if (state.month) {
      return historyMonthKeyForTxn(txn) === state.month;
    }
    return true;
  }
  return true;
}

function matchesHistoryActionFilter(txn: HistoryTxn, action: string): boolean {
  if (!action || action === "all") return true;
  return historyActionLabel(txn) === action;
}

/** Rows with no account are live book entries. Paper mode and a picked account drop them. */
function historyTxnInScope(txn: HistoryTxn): boolean {
  const id = String(txn.account_id ?? "").trim();
  if (!id) return scopeAllowsAccount(undefined);
  return scopeAllowsAccount(id, txn.broker_id);
}

/** Own-ledger Br / Ac cells open the account switcher. */
function withHistoryAccountReassignAttr(cellHtml: string, txn: HistoryTxn, id: number): string {
  if (mountedLedgerSource !== "own_ledger" || !id) return cellHtml;
  if (isImpliedStockLeg(txn)) return cellHtml;
  const attr = isCashFlowHistoryRow(txn)
    ? ` data-hist-reassign-cash="${Math.abs(id)}"`
    : ` data-hist-reassign-txn="${id}"`;
  return cellHtml
    .replace(/\bclass="/, 'class="sfe-hist-reassign-account ')
    .replace(/^(<td\b[^>]*)/, `$1${attr} title="Change broker / account"`);
}

function scopedHistoryTransactions(transactions: HistoryTxn[]): HistoryTxn[] {
  return transactions.filter(historyTxnInScope);
}

function applyFilters(transactions: HistoryTxn[], state: HistoryFilterState): HistoryTxn[] {
  return transactions.filter((txn) => {
    if (!historyTxnInScope(txn)) return false;
    if (isDeletedCashFlowRow(txn)) return false;
    if (!matchesHistoryTypeFilterExtended(txn, state.type)) return false;
    if (state.side !== "all" && !matchesHistorySideFilter(txn, state.side)) return false;
    if (!matchesHistoryTickerFilter(txn, state.ticker)) return false;
    if (!matchesHistoryActionFilter(txn, state.action)) return false;
    if (!matchesDateFilter(txn, state)) return false;
    return true;
  });
}

function sortTransactions(transactions: HistoryTxn[], ledgerSource: LedgerSource): HistoryTxn[] {
  if (ledgerSource === "own_ledger") {
    const byId = buildTxnById(transactions);
    return [...transactions].sort((a, b) =>
      compareOwnLedgerHistoryDisplayRows(a, b, { byId, all: transactions }),
    );
  }
  return [...transactions].sort((a, b) => compareHistoryRowsForDisplay(a, b, "desc"));
}

function fmtUsd(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtStrike(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(2);
}

function fmtSignedUsd(value: number): string {
  const prefix = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${prefix}${fmtUsd(Math.abs(value))}`;
}

function fmtShareLot(shares: number, avg: number): string {
  const qty = `${Math.max(0, Math.round(shares)).toLocaleString("en-US")}sh`;
  return shares > 0 && avg > 0 ? `${qty} (avg ${fmtUsd(avg)})` : qty;
}

/** `100sh (avg $421.00) → 200sh (avg $443.00)`; empty when inventory did not move. */
function fmtSharePositionChange(snapshot: ShareAvgCostSnapshot): string {
  const sameShares = Math.round(snapshot.sharesBefore) === Math.round(snapshot.sharesAfter);
  const sameAvg = Math.abs(snapshot.avgBefore - snapshot.avgAfter) < 0.005;
  if (sameShares && sameAvg) return "";
  return `${fmtShareLot(snapshot.sharesBefore, snapshot.avgBefore)} → ${fmtShareLot(snapshot.sharesAfter, snapshot.avgAfter)}`;
}

/** A balance reads as a plain figure; only a negative one needs a sign. */
function fmtCashBalance(value: number): string {
  return value < 0 ? `−${fmtUsd(Math.abs(value))}` : fmtUsd(value);
}

/** `$384,278 → $354,278` — external cash total either side of this row. */
function fmtCashBalanceChange(snapshot: CashBalanceSnapshot): string {
  return `${fmtCashBalance(snapshot.before)} → ${fmtCashBalance(snapshot.after)}`;
}

function fmtCashAmount(txn: HistoryTxn): string {
  if (!isCashFlowHistoryRow(txn)) return fmtUsd(txn.avg_price);
  const amount = Math.abs(Number(txn.avg_price) || 0);
  if (!amount) return "—";
  const signed = txn.cash_flow_direction === "out" ? -amount : amount;
  const prefix = signed >= 0 ? "+" : "−";
  return `${prefix}${fmtUsd(Math.abs(signed))}`;
}

function fmtQty(txn: HistoryTxn): string {
  if (isCashFlowHistoryRow(txn)) return "—";
  const qty = Math.abs(Number(txn.quantity) || 0);
  if (!qty) return "—";
  return historyInstrumentKind(txn) === "stock" ? qty.toLocaleString("en-US") : String(qty);
}

function isLinkedTxn(txn: HistoryTxn): boolean {
  return txn.linked_transaction_id != null && Number.isFinite(Number(txn.linked_transaction_id));
}

function renderRangeChips(selected: HistoryFilterState["date"]): string {
  const chips: Array<{ value: HistoryFilterState["date"]; label: string }> = [
    { value: "all", label: "All" },
    { value: "7d", label: "7d" },
    { value: "30d", label: "30d" },
  ];
  return chips
    .map(({ value, label }) => {
      const active = selected === value;
      return `<button type="button" class="year-chip${active ? " active" : ""}" data-hist-range="${value}" aria-pressed="${active ? "true" : "false"}">${label}</button>`;
    })
    .join("");
}

function renderYearChips(years: number[], selected: HistoryFilterState["date"]): string {
  const allActive = selected === "all";
  const allChip = `<button type="button" class="${historyYearChipClassName("all", allActive)}" data-hist-year="all" data-tip="Every year on record. Months are per-year, so month selection is off." aria-label="All years" aria-pressed="${allActive ? "true" : "false"}">All</button>`;
  if (!years.length) return `${allChip}<span class="muted small">No years</span>`;
  return (
    allChip +
    years
      .slice(0, 6)
      .map((year) => {
        const active = selected === year;
        return `<button type="button" class="${historyYearChipClassName(year, active)}" data-hist-year="${year}" aria-pressed="${active ? "true" : "false"}">${year}</button>`;
      })
      .join("")
  );
}

function transactionsForMonthIndex(
  transactions: HistoryTxn[],
  state: HistoryFilterState,
): HistoryTxn[] {
  if (state.type === "all" && state.action === "all") return transactions;
  return transactions.filter(
    (txn) =>
      matchesHistoryTypeFilterExtended(txn, state.type) &&
      matchesHistoryActionFilter(txn, state.action),
  );
}

/** Only drops a month that cannot apply to the current range. A month that is
 * merely empty under the active filters is left selected on purpose: repaints
 * run on partially loaded caches, and re-deriving the selection there used to
 * move the user's month on its own during a background refresh. */
function sanitizeHistoryMonthFilter(
  state: HistoryFilterState,
  ledgerSource: LedgerSource,
): void {
  if (typeof state.date !== "number" && state.month) {
    state.month = null;
    saveFilters(state, ledgerSource);
  }
}

function renderMonthChips(
  state: HistoryFilterState,
  transactions: HistoryTxn[],
  years: number[],
): string {
  const allYears = typeof state.date !== "number";
  const year = historyMonthRowYear(state.date, years);
  const activeMonths = monthsWithTransactions(
    transactionsForMonthIndex(transactions, state),
    year,
  );
  return monthKeysForYear(year)
    .map((monthKey) => {
      const inactive = allYears || !activeMonths.has(monthKey);
      const active = !allYears && state.month === monthKey;
      const label = historyMonthShortLabel(monthKey);
      const disabledAttrs = inactive
        ? allYears
          ? ' disabled aria-disabled="true" data-tip="Pick a single year to open a month" aria-label="' +
            escAttr(`${label} (month view needs a year)`) +
            '"'
          : ' disabled aria-disabled="true" data-tip="No trades this month" aria-label="' +
            escAttr(`${label} (no trades)`) +
            '"'
        : ` aria-label="${escAttr(label)}"`;
      return `<button type="button" class="${historyMonthChipClassName(monthKey, active, inactive)}" data-hist-month="${monthKey}" aria-pressed="${active ? "true" : "false"}"${disabledAttrs}>${esc(label)}</button>`;
    })
    .join("");
}

function renderTypeChips(state: HistoryFilterState, ledgerSource: LedgerSource): string {
  const cashChip =
    ledgerSource === "own_ledger"
      ? `<button type="button" class="year-chip${state.type === "cash" ? " active" : ""}" data-hist-type="cash">Cash</button>`
      : "";
  return (
    `<button type="button" class="year-chip${state.type === "all" ? " active" : ""}" data-hist-type="all">All</button>` +
    `<button type="button" class="year-chip${state.type === "option" ? " active" : ""}" data-hist-type="option">Options</button>` +
    `<button type="button" class="year-chip${state.type === "stock" ? " active" : ""}" data-hist-type="stock">Stocks</button>` +
    cashChip
  );
}

function renderSideChips(state: HistoryFilterState): string {
  return (
    `<button type="button" class="year-chip${state.side === "all" ? " active" : ""}" data-hist-side="all">All</button>` +
    `<button type="button" class="year-chip${state.side === "put" ? " active" : ""}" data-hist-side="put">Puts</button>` +
    `<button type="button" class="year-chip${state.side === "call" ? " active" : ""}" data-hist-side="call">Calls</button>`
  );
}

function renderActionOptions(state: HistoryFilterState, transactions: HistoryTxn[]): string {
  return [
    `<option value="all"${state.action === "all" ? " selected" : ""}>All</option>`,
    ...historyActionCodes(transactions, state.action).map(
      (code) =>
        `<option value="${escAttr(code)}"${code === state.action ? " selected" : ""}>${esc(code)}</option>`,
    ),
  ].join("");
}

/** Side chips only apply to options — hidden inline so the field can stay mounted. */
function sideFieldDisplay(state: HistoryFilterState): string {
  return state.type === "stock" || state.type === "cash" ? "none" : "";
}

function renderFilterBar(
  state: HistoryFilterState,
  years: number[],
  transactions: HistoryTxn[],
  ledgerSource: LedgerSource,
): string {
  const scoped = scopedHistoryTransactions(transactions);
  const scopedYears = extractYears(scoped);
  const monthChips = renderMonthChips(state, scoped, scopedYears.length ? scopedYears : years);
  const allYears = typeof state.date !== "number";
  const sideHidden = sideFieldDisplay(state);
  return `
    <div class="hist-controls-row">
      <div class="filter-bar recs-filter-bar">
        <div class="filter-field">
          <label for="wheelHistTicker">Ticker</label>
          <input type="text" id="wheelHistTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
        </div>
        <div class="filter-field">
          <label>Type</label>
          <div class="year-chips" id="wheelHistTypeFilter" role="group">${renderTypeChips(state, ledgerSource)}</div>
        </div>
        <div class="filter-field" id="wheelHistSideField"${sideHidden ? ` style="display:${sideHidden}"` : ""}>
          <label>Side</label>
          <div class="year-chips" id="wheelHistSideFilter" role="group">${renderSideChips(state)}</div>
        </div>
        <div class="filter-field">
          <label for="wheelHistAction">Action</label>
          <select id="wheelHistAction" class="filter-input-compact">${renderActionOptions(state, scoped)}</select>
        </div>
        <div class="filter-field">
          <label>Range</label>
          <div class="year-chips" id="wheelHistRangeFilter" role="group">${renderRangeChips(state.date)}</div>
        </div>
        <div class="filter-field">
          <div class="sfe-filter-reset-actions">
            <button type="button" class="small sfe-reset-filters" id="wheelHistResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          </div>
        </div>
      </div>
    </div>
    <div class="sfe-history-date-filters" data-all-years="${allYears ? "true" : "false"}">
      <div class="sfe-income-year-row">
        <span class="muted small sfe-income-year-label">Year</span>
        <div class="year-chips sfe-income-year-chips" id="wheelHistYearFilter" role="group">${renderYearChips(scopedYears.length ? scopedYears : years, state.date)}</div>
      </div>
      <div class="sfe-income-month-selector" id="wheelHistMonthFilter" role="tablist" aria-label="Month">${monthChips}</div>
    </div>`;
}

function historyTableId(ledgerSource: LedgerSource): typeof TABLE_ID | typeof TABLE_ID_LOCAL {
  return ledgerSource === "own_ledger" ? TABLE_ID_LOCAL : TABLE_ID;
}

/** Keeps an estimated underlying price from reading like a measured one. */
function underlyingPriceNote(source: string | null | undefined): {
  estimated: boolean;
  tip: string;
} {
  switch (source) {
    case "md_fill_minute":
      return { estimated: false, tip: "Underlying at the minute the order filled" };
    case "md_day_close":
      return {
        estimated: false,
        tip: "Underlying at the session close, where the contract settled",
      };
    case "md_day_close_estimated":
      return {
        estimated: true,
        tip: "Estimated — the fill minute was unavailable, so the session close stands in",
      };
    default:
      return { estimated: false, tip: "Underlying price recorded with this row" };
  }
}

function renderUnderlyingPriceCell(
  colId: string,
  txn: HistoryTxn,
  isOption: boolean,
  kind: "open" | "close",
): string {
  const blank = `<td class="num" data-col="${colId}">—</td>`;
  if (!isOption) return blank;
  const value = kind === "open" ? txn.underlying_open_price : txn.underlying_close_price;
  if (!(Number(value) > 0)) return blank;
  // A row stores only its own spot, so its recorded source describes the open price
  // on an opening row and the close price on a closing row.
  const ownSide = txn.close_reason || txn.action === "buy" ? "close" : "open";
  const note = underlyingPriceNote(kind === ownSide ? txn.underlying_price_source : undefined);
  const tip =
    kind === ownSide
      ? note.tip
      : kind === "open"
        ? "Underlying when the contract was opened, from the opening leg"
        : "Underlying when the contract settled, from the closing leg";
  const text = `${note.estimated ? "≈" : ""}${fmtUsd(value)}`;
  return `<td class="num" data-col="${colId}" title="${escAttr(tip)}">${esc(text)}</td>`;
}

/**
 * Odds the contract was written at. Long opens have none: the figure is
 * 1 − |delta| from the seller's side, so it would read backwards on a buy.
 */
function renderOpenPopCell(colId: string, txn: HistoryTxn, isOption: boolean): string {
  const blank = `<td class="num" data-col="${colId}">—</td>`;
  if (!isOption) return blank;
  const pop = Number(txn.open_pop);
  if (!(pop > 0)) {
    if (txn.open_pop_source !== "not_captured") return blank;
    return `<td class="num" data-col="${colId}" title="${escAttr("Opened before POP was recorded — historical greeks are not published, so this cannot be filled in")}">—</td>`;
  }
  const ownsOpen = !txn.close_reason && txn.action === "sell";
  const tip = ownsOpen
    ? "Probability of profit when this contract was sold (1 − |delta|)"
    : "Probability of profit its opening leg was sold at (1 − |delta|)";
  return `<td class="num" data-col="${colId}" title="${escAttr(tip)}">${esc(`${(pop * 100).toFixed(1)}%`)}</td>`;
}

function renderHistoryCell(
  colId: string,
  txn: HistoryTxn,
  opts: {
    linkedExpanded?: boolean;
    linkedDetail?: boolean;
    detailKind?: HistoryDetailKind;
    stockAvgById?: ReadonlyMap<number, StockLegAvgCostDetail>;
    shareAvgById?: ReadonlyMap<number, ShareAvgCostSnapshot>;
    cashBalanceById?: ReadonlyMap<number, CashBalanceSnapshot>;
  } = {},
): string {
  const id = Number(txn.id) || 0;
  const isOption = historyInstrumentKind(txn) === "option";
  const isStock = historyInstrumentKind(txn) === "stock";
  const isCash = historyInstrumentKind(txn) === "cash";
  const side = (txn.side ?? "").toLowerCase();
  const typePill = isOption
    ? `<span class="hist-pill hist-option">option</span>`
    : isStock
      ? `<span class="hist-pill hist-stock">stock</span>`
      : isCash
        ? `<span class="hist-pill hist-cash">cash</span>`
        : `<span class="hist-pill">—</span>`;
  const actionPill = renderHistoryActionPill(txn);
  const sideCell =
    side === "call"
      ? `<span class="side-badge side-call">CALL</span>`
      : side === "put"
        ? `<span class="side-badge side-put">PUT</span>`
        : "—";
  const linked = isLinkedTxn(txn);
  const implied = isImpliedStockLeg(txn);

  switch (colId) {
    case "trade_date": {
      const date = esc(String(txn.trade_date ?? "—"));
      if (opts.linkedDetail) {
        return `<td data-col="trade_date"><span class="sfe-hist-linked-tree" aria-hidden="true">↳</span>${date}</td>`;
      }
      return `<td data-col="trade_date">${date}</td>`;
    }
    case "instrument_type":
      return `<td data-col="instrument_type">${typePill}</td>`;
    case "action":
      return `<td data-col="action">${actionPill}</td>`;
    case "ticker": {
      if (isCash) {
        return `<td class="tk-cell" data-col="ticker">—</td>`;
      }
      return `<td class="tk-cell" data-col="ticker"><span class="recs-ticker" data-ticker="${escAttr(txn.ticker ?? "")}" title="Open ${escAttr(txn.ticker ?? "")} on Stock Forge">${esc(txn.ticker ?? "—")}<span class="tk-ic">↗</span></span></td>`;
    }
    case "notes": {
      const notes = historyTransactionNotes(txn);
      const pending = historyPendingSettlementDisplay(txn);
      const statusParts: string[] = [];
      if (pending) {
        statusParts.push(
          `<span class="sfe-hist-status-note sfe-hist-pending-settlement">${esc(pending)}</span>`,
        );
      }
      if (implied) {
        statusParts.push(
          `<span class="sfe-hist-status-note sfe-hist-from-assignment">From assignment<span class="hint" tabindex="0" role="img" data-tip="Inferred stock leg from a covered-call or cash-secured put assignment. Not stored as a separate ledger row on Stock Forge.">i</span></span>`,
        );
      }
      const origin =
        mountedLedgerSource === "own_ledger"
          ? historyRowOrigin(txn as HistoryLedgerMetaTxn)
          : null;
      if (origin) {
        statusParts.push(
          `<span class="sfe-hist-status-note sfe-hist-derived-row sfe-hist-origin-${origin.tone}">${esc(origin.label)}<span class="hint" tabindex="0" role="img" data-tip="${escAttr(origin.tip)}">i</span></span>`,
        );
      }
      const statusHtml = statusParts.join(`<span class="sfe-hist-notes-sep"> · </span>`);
      if (statusHtml && notes) {
        return `<td data-col="notes" class="sfe-hist-notes">${statusHtml}<span class="sfe-hist-notes-sep"> · </span>${esc(notes)}</td>`;
      }
      if (statusHtml) {
        return `<td data-col="notes" class="sfe-hist-notes">${statusHtml}</td>`;
      }
      return `<td data-col="notes" class="sfe-hist-notes">${notes ? esc(notes) : "—"}</td>`;
    }
    case "broker_mark": {
      const cell = brokerMarkCellHtml(ledgerRowMark(txn as HistoryLedgerMetaTxn));
      return withHistoryAccountReassignAttr(cell, txn, id);
    }
    case "account_short": {
      const cell = accountShortCellHtml(ledgerRowMark(txn as HistoryLedgerMetaTxn));
      return withHistoryAccountReassignAttr(cell, txn, id);
    }
    case "system":
    case "broker": {
      const label = historySystemLabel(txn as HistoryLedgerMetaTxn);
      return `<td data-col="${colId === "broker" ? "broker" : "system"}" class="sfe-hist-ledger-meta">${label ? esc(label) : "—"}</td>`;
    }
    case "account": {
      const label = historyAccountLabel(txn as HistoryLedgerMetaTxn & { account_name?: string | null });
      return `<td data-col="account" class="sfe-hist-ledger-meta">${label ? esc(label) : "—"}</td>`;
    }
    case "side":
      return `<td data-col="side">${sideCell}</td>`;
    case "quantity":
      return `<td class="num" data-col="quantity">${fmtQty(txn)}</td>`;
    case "avg_price":
      return `<td class="num" data-col="avg_price">${isCash ? fmtCashAmount(txn) : fmtUsd(txn.avg_price)}</td>`;
    case "holding_avg": {
      if (!isStock || !id) {
        return `<td class="num" data-col="holding_avg">—</td>`;
      }
      const snap = opts.stockAvgById?.get(id);
      const text = snap ? formatStockHoldingAvgChange(snap) : "";
      if (!text) return `<td class="num" data-col="holding_avg">—</td>`;
      const tip =
        snap?.action === "buy"
          ? "Holding average before → after this buy"
          : "Holding average before this sale";
      return `<td class="num sfe-hist-holding-avg" data-col="holding_avg" title="${escAttr(tip)}">${esc(text)}</td>`;
    }
    case "share_avg": {
      const snapshot = id ? opts.shareAvgById?.get(id) : undefined;
      const avg = snapshot?.assignAvg ?? snapshot?.avgBefore ?? 0;
      if (!isOption || side !== "call" || !(avg > 0)) {
        return `<td class="num" data-col="share_avg">—</td>`;
      }
      const tip = snapshot?.assignAvg
        ? "Average share cost charged against the strike on this assignment"
        : "Average cost of the shares backing this call";
      return `<td class="num" data-col="share_avg" title="${escAttr(tip)}">${esc(fmtUsd(avg))}</td>`;
    }
    case "assign_pl": {
      const snapshot = id ? opts.shareAvgById?.get(id) : undefined;
      const pnl = snapshot?.assignPnl;
      if (pnl == null || !Number.isFinite(pnl)) {
        return `<td class="num" data-col="assign_pl">—</td>`;
      }
      const tone = pnl > 0 ? " sfe-pos" : pnl < 0 ? " sfe-neg" : "";
      const shares = Math.round(snapshot?.assignShares ?? 0);
      const tip = `(strike ${fmtStrike(txn.strike)} − avg ${fmtUsd(snapshot?.assignAvg ?? 0)}) × ${shares.toLocaleString("en-US")} shares`;
      return `<td class="num${tone}" data-col="assign_pl" title="${escAttr(tip)}">${esc(fmtSignedUsd(pnl))}</td>`;
    }
    case "share_pos": {
      if (isCash) {
        const cash = id ? opts.cashBalanceById?.get(id) : undefined;
        if (!cash) return `<td data-col="share_pos">—</td>`;
        return `<td class="sfe-hist-share-pos" data-col="share_pos" title="External cash total before → after this row (deposits, withdrawals, transfers and interest only)">${esc(fmtCashBalanceChange(cash))}</td>`;
      }
      // Share inventory lives on the stock leg. Option ASGN rows move the same
      // shares, so showing the before → after there just duplicates the companion
      // stock transaction that already carries it.
      if (!isStock) return `<td data-col="share_pos">—</td>`;
      const snapshot = id ? opts.shareAvgById?.get(id) : undefined;
      const text = snapshot ? fmtSharePositionChange(snapshot) : "";
      if (!text) return `<td data-col="share_pos">—</td>`;
      return `<td class="sfe-hist-share-pos" data-col="share_pos" title="Share position before → after this row">${esc(text)}</td>`;
    }
    case "underlying_open_price":
      return renderUnderlyingPriceCell(colId, txn, isOption, "open");
    case "underlying_close_price":
      return renderUnderlyingPriceCell(colId, txn, isOption, "close");
    case "open_pop":
      return renderOpenPopCell(colId, txn, isOption);
    case "strike":
      return `<td class="num" data-col="strike">${isOption ? fmtStrike(txn.strike) : "—"}</td>`;
    case "exp_date":
      return `<td data-col="exp_date">${isOption ? esc(String(txn.exp_date ?? "—")) : "—"}</td>`;
    case "actions": {
      if (opts.linkedDetail) {
        return `<td class="pos-actions" data-col="actions"><span class="sfe-hist-linked sfe-hist-linked-related">${opts.detailKind === "opening" ? "Opening leg" : "Related leg"}</span></td>`;
      }
      if (implied) {
        return `<td class="pos-actions" data-col="actions"></td>`;
      }
      if (isCash) {
        if (mountedLedgerSource === "own_ledger" && id < 0) {
          const cashFlowId = Math.abs(id);
          // Restoring is harmless, so a deleted row offers it outright rather than
          // sending the user through the edit modal to find it.
          return isDeletedCashFlowRow(txn)
            ? `<td class="pos-actions" data-col="actions"><button type="button" class="ghost small sfe-hist-cash-restore" data-cash-flow-id="${cashFlowId}" title="Put this cash transaction back">Restore</button></td>`
            : `<td class="pos-actions" data-col="actions"><button type="button" class="ghost small sfe-hist-cash-edit" data-cash-flow-id="${cashFlowId}" title="Edit type and comment, or delete">Edit</button></td>`;
        }
        return `<td class="pos-actions" data-col="actions"></td>`;
      }
      if (!id) return `<td class="pos-actions" data-col="actions"></td>`;
      const callAssign =
        txn.instrument_type === "option" &&
        txn.action === "buy" &&
        txn.side === "call" &&
        txn.close_reason === "assigned";
      return `<td class="pos-actions" data-col="actions">${renderFocusHistoryRowMenu({
        txnId: id,
        linked,
        linkedExpanded: opts.linkedExpanded,
        callAssign,
        canReassignAccount: mountedLedgerSource === "own_ledger",
      })}</td>`;
    }
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

/** Replay-derived per-row context the table needs on every render path. */
interface HistoryRowIndexes {
  stockAvgById: ReadonlyMap<number, StockLegAvgCostDetail>;
  shareAvgById: ReadonlyMap<number, ShareAvgCostSnapshot>;
  cashBalanceById: ReadonlyMap<number, CashBalanceSnapshot>;
}

/**
 * Avg cost for stock rows read off the share replay, so the Avg cost, Share avg
 * and Shares columns cannot tell different stories. Rows the replay does not move
 * (an implied leg whose assignment was settled by a real lot) report nothing;
 * assignment sells borrow the average their call leg was priced at, since the
 * call consumes the shares before the stock row is reached.
 */
function stockAvgFromShareSnapshots(
  all: HistoryTxn[],
  shareAvgById: ReadonlyMap<number, ShareAvgCostSnapshot>,
): Map<number, StockLegAvgCostDetail> {
  const index = new Map<number, StockLegAvgCostDetail>();
  for (const txn of all) {
    if (historyInstrumentKind(txn) !== "stock") continue;
    const action = txn.action === "buy" || txn.action === "sell" ? txn.action : null;
    if (!action) continue;
    const id = Number(txn.id) || 0;
    const snapshot = shareAvgById.get(id);
    if (!snapshot) continue;

    const moved = Math.round(snapshot.sharesBefore) !== Math.round(snapshot.sharesAfter);
    let beforeAvg = snapshot.avgBefore;
    if (action === "sell" && !(beforeAvg > 0)) {
      beforeAvg = shareAvgById.get(Number(txn.linked_transaction_id) || 0)?.assignAvg ?? 0;
    } else if (action === "buy" && !moved) {
      continue;
    }
    index.set(id, {
      action,
      fillPrice: Number(txn.avg_price) || 0,
      beforeAvg,
      afterAvg: snapshot.avgAfter,
    });
  }
  return index;
}

function buildHistoryRowIndexes(all: HistoryTxn[]): HistoryRowIndexes {
  // Implied legs are a display convenience History adds on top of the ledger.
  // Feeding them to the replay would count assignment shares twice and put these
  // columns out of step with Analytics(L), so they inherit the option's snapshot.
  const shareAvgById = new Map(buildShareAvgCostSnapshots(all.filter((txn) => !isImpliedStockLeg(txn))));
  for (const txn of all) {
    if (!isImpliedStockLeg(txn)) continue;
    const id = Number(txn.id) || 0;
    const source = Number(txn.linked_transaction_id ?? -id) || 0;
    const snapshot = shareAvgById.get(source);
    if (snapshot && !shareAvgById.has(id)) shareAvgById.set(id, snapshot);
  }
  return {
    stockAvgById:
      mountedLedgerSource === "own_ledger"
        ? stockAvgFromShareSnapshots(all, shareAvgById)
        : buildStockLegAvgCostDetailIndex(all),
    shareAvgById,
    cashBalanceById: buildCashBalanceSnapshots(all),
  };
}

function renderTableBody(
  filtered: HistoryTxn[],
  columns: ColumnState[],
  byId: Map<number, HistoryTxn>,
  all: HistoryTxn[],
  byContract: Map<string, HistoryTxn[]>,
): string {
  const visible = visibleTableColumns(columns);
  if (!filtered.length) {
    return `<tr><td colspan="${Math.max(visible.length, 1)}" class="muted">No transactions match these filters.</td></tr>`;
  }
  const indexes = buildHistoryRowIndexes(all);
  const rows: string[] = [];
  for (const txn of filtered) {
    const id = Number(txn.id) || 0;
    const expanded = id > 0 && expandedLinkedTxnIds.has(id);
    const cells = visible
      .map((column) => renderHistoryCell(column.key, txn, { linkedExpanded: expanded, ...indexes }))
      .join("");
    const deletedClass = isDeletedCashFlowRow(txn) ? ' class="sfe-hist-row-deleted"' : "";
    rows.push(`<tr${deletedClass} data-txn-id="${id}">${cells}</tr>`);

    if (expanded) {
      const details = findHistoryDetailRows(txn, byId, all, byContract);
      details.forEach((detail, index) => {
        const edgeClass =
          details.length === 1
            ? "sfe-hist-linked-detail-first sfe-hist-linked-detail-last"
            : index === 0
              ? "sfe-hist-linked-detail-first"
              : index === details.length - 1
                ? "sfe-hist-linked-detail-last"
                : "";
        const detailCells = visible
          .map((column) =>
            renderHistoryCell(column.key, detail.txn as HistoryTxn, {
              linkedDetail: true,
              detailKind: detail.kind,
              ...indexes,
            }),
          )
          .join("");
        rows.push(
          `<tr class="sfe-hist-linked-detail sfe-hist-linked-detail-${detail.kind}${edgeClass ? ` ${edgeClass}` : ""}" data-linked-from="${id}" data-txn-id="${Number(detail.txn.id) || 0}">${detailCells}</tr>`,
        );
      });
    }
  }
  return rows.join("");
}

function renderPanelShell(
  state: HistoryFilterState,
  cache: HistoryPanelCache,
  filtered: HistoryTxn[],
  columns: ColumnState[],
  stale = false,
  ledgerSource: LedgerSource = "stockforge",
): string {
  const staleNote = stale ? " · showing cached data, refreshing…" : "";
  const title =
    ledgerSource === "own_ledger" ? "Transaction history (SF+ ledger)" : "Transaction history";
  const byId = buildTxnById(cache.transactions);
  const byContract = buildOptionSymbolIndex(cache.transactions);
  const body = renderTableBody(filtered, columns, byId, cache.transactions, byContract);

  return `
    <div data-wheel-view="${ledgerSource === "own_ledger" ? "history-local" : "history"}" class="panel sfe-web-native-history" style="margin-top:12px;">
      <div class="section-head" style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <div>
          <h2 class="insight-h" style="margin:0 0 4px;">${title}</h2>
          <p class="muted small" style="margin:0;">Trade dates use US/Eastern (NYSE session calendar).</p>
        </div>
      </div>
      ${renderFilterBar(state, cache.years, cache.transactions, ledgerSource)}
      <p class="muted small recs-filter-note" id="wheelHistFilterNote">${filtered.length.toLocaleString("en-US")} transaction${filtered.length === 1 ? "" : "s"}${staleNote}</p>
      <div class="recs-wrap" data-tour-anchor="wheel-history-table">
        <div class="recs-hscroll" id="wheelHistoryHScroll">
          <table class="recs recs-compact wheel-screener-history" id="wheelHistoryTable"${renderTablePinnedAttr(columns)}>
            ${renderTableColgroup(columns)}
            <thead>
              <tr>${renderTableHeadRow(columns)}</tr>
            </thead>
            <tbody id="wheelHistoryBody">${body}</tbody>
          </table>
        </div>
      </div>
    </div>`;
}

function bindHistoryTxnMenu(): void {
  if (historyMenuBound) return;
  historyMenuBound = true;

  document.addEventListener(HISTORY_TXN_MENU_EVENT, (event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as {
      action?: HistoryTxnMenuAction;
      txnId?: number;
      linked?: boolean;
      linkedExpanded?: boolean;
    };
    const action = detail.action;
    const txnId = Number(detail.txnId);
    if (!action || !(txnId > 0)) return;
    const ledgerSource = mountedLedgerSource;
    const cache = activeHistoryCache();
    const rerender = historyPanelRerender;

    if (action === "toggle-linked") {
      if (expandedLinkedTxnIds.has(txnId)) expandedLinkedTxnIds.delete(txnId);
      else expandedLinkedTxnIds.add(txnId);
      rerender?.();
      return;
    }

    if (action === "add") {
      const txn = cache?.transactions.find((row) => Number(row.id) === txnId);
      if (!txn) {
        showToast("Transaction not found. Refresh and try again.", "error");
        return;
      }
      openHistoryTransactionAddModal({
        defaults: historyTransactionAddDefaultsFromTxn(txn),
        onSaved: async () => {
          invalidateHistoryPanelCache(ledgerSource);
          if (mountedPanel) {
            await renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource });
          }
        },
      });
      return;
    }

    if (action === "link-wheel-pair") {
      const txn = cache?.transactions.find((row) => Number(row.id) === txnId);
      if (!txn) {
        showToast("Transaction not found. Refresh and try again.", "error");
        return;
      }
      openWheelPairLinkModal(
        {
          id: txnId,
          instrument_type: txn.instrument_type,
          action: txn.action,
          side: txn.side,
          ticker: txn.ticker,
          trade_date: txn.trade_date,
          exp_date: txn.exp_date,
          quantity: txn.quantity,
          strike: txn.strike,
          close_reason: txn.close_reason,
          wheel_pair_linked_ids: (txn as { wheel_pair_linked_ids?: number[] }).wheel_pair_linked_ids,
        },
        {
          onSaved: async () => {
            invalidateHistoryPanelCache(ledgerSource);
            if (mountedPanel) {
              await renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource });
            }
          },
        },
      );
      return;
    }

    if (action === "edit") {
      const txn = cache?.transactions.find((row) => Number(row.id) === txnId);
      if (!txn) {
        showToast("Transaction not found. Refresh and try again.", "error");
        return;
      }
      openHistoryTransactionEditModal(
        {
          id: txnId,
          instrument_type: txn.instrument_type,
          action: txn.action,
          side: txn.side,
          ticker: txn.ticker,
          trade_date: txn.trade_date,
          quantity: txn.quantity,
          avg_price: txn.avg_price,
          strike: txn.strike,
          exp_date: txn.exp_date,
          option_symbol: txn.option_symbol,
          close_reason: txn.close_reason,
          linked_transaction_id: txn.linked_transaction_id,
        },
        {
          onSaved: async () => {
            invalidateHistoryPanelCache(ledgerSource);
            if (mountedPanel) {
              await renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource });
            }
          },
        },
      );
      return;
    }

    if (action === "change-account") {
      if (ledgerSource !== "own_ledger") {
        showToast("Account changes are only available on History (SF+ ledger).", "error");
        return;
      }
      const txn = cache?.transactions.find((row) => Number(row.id) === txnId);
      if (!txn) {
        showToast("Transaction not found. Refresh and try again.", "error");
        return;
      }
      void (async () => {
        const changed = await promptHistoryAccountReassign({
          kind: "transaction",
          id: txnId,
          currentAccountId: txn.account_id,
        });
        if (!changed) return;
        invalidateHistoryPanelCache(ledgerSource);
        if (mountedPanel) {
          await renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource });
        }
      })();
      return;
    }

    if (action === "delete") {
      void (async () => {
        const confirmed = await confirmDialog({
          title: "Delete transaction",
          message: detail.linked
            ? "Delete this transaction and its linked assignment leg(s)? This cannot be undone."
            : "Delete this transaction? This cannot be undone.",
          confirmLabel: "Delete",
          danger: true,
        });
        if (!confirmed) return;
        try {
          const count = await deleteLedgerTransaction(txnId);
          showToast(`Deleted ${count} transaction${count === 1 ? "" : "s"}.`, "success");
          invalidateHistoryPanelCache(ledgerSource);
          if (mountedPanel) {
            void renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource });
          }
        } catch (err) {
          showToast(err instanceof WheelActionError ? err.message : "Could not delete transaction.", "error");
        }
      })();
    }
  });
}

function bindFilters(
  root: HTMLElement,
  state: HistoryFilterState,
  rerender: () => void,
  ledgerSource: LedgerSource,
  getTransactions: () => HistoryTxn[],
): void {
  root.querySelector("#wheelHistTypeFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hist-type]");
    if (!btn) return;
    const value = btn.dataset.histType;
    if (value === "option" || value === "stock" || value === "cash" || value === "all") {
      state.type = value;
      if (value === "stock" || value === "cash") state.side = "all";
      sanitizeHistoryMonthFilter(state, ledgerSource);
      saveFilters(state, ledgerSource);
      rerender();
    }
  });

  root.querySelector<HTMLSelectElement>("#wheelHistAction")?.addEventListener("change", (event) => {
    state.action = (event.target as HTMLSelectElement).value || "all";
    saveFilters(state, ledgerSource);
    rerender();
  });

  root.querySelector("#wheelHistSideFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hist-side]");
    if (!btn) return;
    const value = btn.dataset.histSide;
    if (value === "put" || value === "call" || value === "all") {
      state.side = value;
      saveFilters(state, ledgerSource);
      rerender();
    }
  });

  root.querySelector("#wheelHistRangeFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hist-range]");
    if (!btn) return;
    const raw = btn.dataset.histRange ?? "all";
    if (raw === "all" || raw === "7d" || raw === "30d") {
      state.date = raw;
      state.month = null;
      saveFilters(state, ledgerSource);
      rerender();
    }
  });

  root.querySelector("#wheelHistYearFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hist-year]");
    if (!btn) return;
    const raw = btn.dataset.histYear ?? "";
    if (raw === "all") {
      state.date = "all";
      state.month = null;
      saveFilters(state, ledgerSource);
      rerender();
      return;
    }
    const year = Number(raw);
    if (!Number.isFinite(year)) return;
    state.date = year;
    const activeMonths = monthsWithTransactions(
      transactionsForMonthIndex(scopedHistoryTransactions(getTransactions()), state),
      year,
    );
    if (state.month?.startsWith(`${year}-`) && activeMonths.has(state.month)) {
      /* keep current month when re-selecting the same year */
    } else {
      state.month = resolveDefaultHistoryMonth(year, activeMonths);
    }
    saveFilters(state, ledgerSource);
    rerender();
  });

  // Delegated from the date-filter host: the month row is created and dropped as
  // the selected year changes.
  root.querySelector(".sfe-history-date-filters")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hist-month]");
    if (!btn || btn.disabled) return;
    const month = btn.dataset.histMonth;
    if (!month || month === state.month) return;
    state.month = month;
    state.date = Number(month.slice(0, 4));
    saveFilters(state, ledgerSource);
    rerender();
  });

  root.querySelector<HTMLInputElement>("#wheelHistTicker")?.addEventListener("input", (event) => {
    state.ticker = sanitizeTickerFilterValue((event.target as HTMLInputElement).value).toUpperCase();
    saveFilters(state, ledgerSource);
    rerender();
  });

  guardTickerFilterInputs(root);

  root.querySelector("#wheelHistResetFilters")?.addEventListener("click", () => {
    Object.assign(state, defaultFilters());
    saveFilters(state, ledgerSource);
    rerender();
  });
}

function bindHistoryTxnNotes(): void {
  ensureTxnNoteClickBinding();
  if (historyTxnNotesBound) return;
  historyTxnNotesBound = true;
  document.addEventListener(WHEEL_LEDGER_CHANGED_EVENT, () => {
    if (!mountedPanel || mountedLedgerSource !== "own_ledger") return;
    invalidateHistoryPanelCache("own_ledger");
    void renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource: "own_ledger" });
  });
}

function bindHistoryCashEdit(): void {
  if (historyCashEditBound) return;
  historyCashEditBound = true;

  const refresh = async (): Promise<void> => {
    invalidateHistoryPanelCache("own_ledger");
    if (mountedPanel) {
      await renderNativeHistoryPanel(mountedPanel, { force: true, ledgerSource: "own_ledger" });
    }
  };

  document.addEventListener("click", (event) => {
    if (mountedLedgerSource !== "own_ledger") return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const reassign = target.closest<HTMLElement>("[data-hist-reassign-txn], [data-hist-reassign-cash]");
    if (reassign) {
      event.preventDefault();
      const cache = activeHistoryCache();
      const txnId = Number(reassign.dataset.histReassignTxn);
      const cashId = Number(reassign.dataset.histReassignCash);
      void (async () => {
        if (Number.isFinite(txnId) && txnId > 0) {
          const txn = cache?.transactions.find((row) => Number(row.id) === txnId);
          if (!txn) {
            showToast("Transaction not found. Refresh and try again.", "error");
            return;
          }
          const changed = await promptHistoryAccountReassign({
            kind: "transaction",
            id: txnId,
            currentAccountId: txn.account_id,
          });
          if (changed) await refresh();
          return;
        }
        if (Number.isFinite(cashId) && cashId > 0) {
          const txn = cache?.transactions.find((row) => Number(row.id) === -cashId);
          if (!txn) {
            showToast("Cash flow not found. Refresh and try again.", "error");
            return;
          }
          const changed = await promptHistoryAccountReassign({
            kind: "cash",
            id: cashId,
            currentAccountId: txn.account_id,
          });
          if (changed) await refresh();
        }
      })();
      return;
    }

    const btn = target.closest<HTMLButtonElement>(".sfe-hist-cash-edit, .sfe-hist-cash-restore");
    if (!btn) return;
    event.preventDefault();
    const cashFlowId = Number(btn.dataset.cashFlowId);
    if (!Number.isFinite(cashFlowId) || cashFlowId <= 0) return;

    if (btn.classList.contains("sfe-hist-cash-restore")) {
      void setOwnLedgerCashFlowDeleted(cashFlowId, false)
        .then(async () => {
          showToast("Cash flow restored.", "success");
          await refresh();
        })
        .catch(() => showToast("Could not restore cash flow.", "error"));
      return;
    }

    const cache = activeHistoryCache();
    const txn = cache?.transactions.find((row) => Number(row.id) === -cashFlowId);
    if (!txn) {
      showToast("Cash flow not found. Refresh and try again.", "error");
      return;
    }
    openHistoryCashFlowEditModalFromTxn(txn, { onSaved: refresh });
  });
}

function paintHistoryLoadError(
  host: HTMLElement,
  err: unknown,
  ledgerSource: LedgerSource,
): void {
  paintPanelLoadError(host, err, {
    title: ledgerSource === "own_ledger" ? "History (SF+ ledger)" : "Transaction history",
    view: ledgerSource === "own_ledger" ? "history-local" : "history",
    fallback: "Could not load transaction history.",
    headerOnly: true,
  });
}

function panelHasDisplayedHistory(panel: HTMLElement): boolean {
  return panel.querySelector(".sfe-web-native-history") != null;
}

/** Last markup written into a container, so unchanged regions are left untouched. */
const renderedContainerHtml = new WeakMap<Element, string>();

/** Replace a container's children only when the markup actually changed, keeping
 *  keyboard focus on the same child position (chip rows re-render on every click). */
function setContainerHtml(host: Element, html: string): void {
  const known = renderedContainerHtml.get(host);
  if (known === html) return;
  if (known === undefined && host.innerHTML === html) {
    renderedContainerHtml.set(host, html);
    return;
  }
  renderedContainerHtml.set(host, html);
  const active = document.activeElement;
  const focusedIndex =
    active instanceof HTMLElement && host.contains(active) ? [...host.children].indexOf(active) : -1;
  host.innerHTML = html;
  if (focusedIndex >= 0) {
    const restored = host.children[focusedIndex];
    if (restored instanceof HTMLElement) restored.focus();
  }
}

function syncHistoryMonthChips(
  root: HTMLElement,
  state: HistoryFilterState,
  transactions: HistoryTxn[],
  years: number[],
): void {
  const host = root.querySelector<HTMLElement>(".sfe-history-date-filters");
  if (!host) return;
  host.dataset.allYears = typeof state.date !== "number" ? "true" : "false";
  const html = renderMonthChips(state, transactions, years);
  let chips = host.querySelector<HTMLElement>("#wheelHistMonthFilter");
  if (!chips) {
    chips = document.createElement("div");
    chips.className = "sfe-income-month-selector";
    chips.id = "wheelHistMonthFilter";
    chips.setAttribute("role", "tablist");
    chips.setAttribute("aria-label", "Month");
    host.appendChild(chips);
  }
  setContainerHtml(chips, html);
}

/** Bring the mounted filter bar up to date without rebuilding it. */
function syncHistoryFilterBar(
  root: HTMLElement,
  state: HistoryFilterState,
  cache: HistoryPanelCache,
  ledgerSource: LedgerSource,
): void {
  const typeChips = root.querySelector("#wheelHistTypeFilter");
  if (typeChips) setContainerHtml(typeChips, renderTypeChips(state, ledgerSource));

  const sideField = root.querySelector<HTMLElement>("#wheelHistSideField");
  if (sideField) sideField.style.display = sideFieldDisplay(state);
  const sideChips = root.querySelector("#wheelHistSideFilter");
  if (sideChips) setContainerHtml(sideChips, renderSideChips(state));

  const rangeChips = root.querySelector("#wheelHistRangeFilter");
  if (rangeChips) setContainerHtml(rangeChips, renderRangeChips(state.date));

  const scoped = scopedHistoryTransactions(cache.transactions);
  const scopedYears = extractYears(scoped);
  const yearChips = root.querySelector("#wheelHistYearFilter");
  if (yearChips) setContainerHtml(yearChips, renderYearChips(scopedYears.length ? scopedYears : cache.years, state.date));

  syncHistoryMonthChips(root, state, scoped, scopedYears.length ? scopedYears : cache.years);

  const action = root.querySelector<HTMLSelectElement>("#wheelHistAction");
  if (action) {
    setContainerHtml(action, renderActionOptions(state, scoped));
    if (action.value !== state.action) action.value = state.action;
  }

  const ticker = root.querySelector<HTMLInputElement>("#wheelHistTicker");
  if (ticker && document.activeElement !== ticker && ticker.value !== state.ticker) {
    ticker.value = state.ticker;
  }
}

/** Colgroup + header row follow the column picker without a table remount. */
function syncHistoryTableChrome(root: HTMLElement, columns: ColumnState[]): void {
  const table = root.querySelector<HTMLElement>("#wheelHistoryTable");
  if (!table) return;
  const headRow = table.querySelector("thead tr");
  if (headRow) setContainerHtml(headRow, renderTableHeadRow(columns));

  const colgroup = table.querySelector("colgroup");
  if (!colgroup) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = renderTableColgroup(columns).trim();
  const next = tpl.content.firstElementChild;
  if (next && next.outerHTML !== colgroup.outerHTML) colgroup.replaceWith(next);
}

/** Marks a refresh in flight; the row set stays on screen while it runs. */
function markHistoryRefreshing(panel: HTMLElement, refreshing: boolean): void {
  panel
    .querySelector<HTMLElement>(".sfe-web-native-history")
    ?.classList.toggle("sfe-hist-refreshing", refreshing);
}

function morphHistoryTableCell(td: HTMLTableCellElement, nextHtml: string): boolean {
  const tpl = document.createElement("template");
  tpl.innerHTML = nextHtml.trim();
  const next = tpl.content.firstElementChild;
  if (!(next instanceof HTMLTableCellElement)) return false;
  if (td.outerHTML === next.outerHTML) return false;

  for (const attr of [...td.attributes]) {
    if (!next.hasAttribute(attr.name)) td.removeAttribute(attr.name);
  }
  for (const attr of next.attributes) {
    td.setAttribute(attr.name, attr.value);
  }
  td.innerHTML = next.innerHTML;
  return true;
}

function patchHistoryParentRow(
  tr: HTMLTableRowElement,
  txn: HistoryTxn,
  visible: ColumnState[],
  linkedExpanded: boolean,
  indexes: HistoryRowIndexes,
): void {
  for (const column of visible) {
    if (column.key === "actions") continue;
    const td = tr.querySelector<HTMLTableCellElement>(`td[data-col="${column.key}"]`);
    if (!td) continue;
    morphHistoryTableCell(td, renderHistoryCell(column.key, txn, { linkedExpanded, ...indexes }));
  }
  // Refresh action menu state (linked expand) without remounting the whole table.
  const actionsTd = tr.querySelector<HTMLTableCellElement>(`td[data-col="actions"]`);
  if (actionsTd) {
    morphHistoryTableCell(
      actionsTd,
      renderHistoryCell("actions", txn, { linkedExpanded }),
    );
  }
}

function insertLinkedDetailRows(
  tr: HTMLTableRowElement,
  txn: HistoryTxn,
  visible: ColumnState[],
  byId: Map<number, HistoryTxn>,
  all: HistoryTxn[],
  byContract: Map<string, HistoryTxn[]>,
  indexes: HistoryRowIndexes,
): void {
  const id = Number(txn.id) || 0;
  const details = findHistoryDetailRows(txn, byId, all, byContract);
  let insertAfter: Element = tr;
  details.forEach((detail, index) => {
    const edgeClass =
      details.length === 1
        ? "sfe-hist-linked-detail-first sfe-hist-linked-detail-last"
        : index === 0
          ? "sfe-hist-linked-detail-first"
          : index === details.length - 1
            ? "sfe-hist-linked-detail-last"
            : "";
    const detailCells = visible
      .map((column) =>
        renderHistoryCell(column.key, detail.txn as HistoryTxn, {
          linkedDetail: true,
          detailKind: detail.kind,
          ...indexes,
        }),
      )
      .join("");
    const row = document.createElement("tr");
    row.className = `sfe-hist-linked-detail sfe-hist-linked-detail-${detail.kind}${edgeClass ? ` ${edgeClass}` : ""}`;
    row.dataset.linkedFrom = String(id);
    row.dataset.txnId = String(Number(detail.txn.id) || 0);
    row.innerHTML = detailCells;
    insertAfter.insertAdjacentElement("afterend", row);
    insertAfter = row;
  });
}

/** Rebuild a row's cell skeleton after a column visibility/order change. */
function syncHistoryRowColumns(row: HTMLTableRowElement, visible: ColumnState[]): void {
  const keys = [...row.children].map((cell) =>
    cell instanceof HTMLElement ? (cell.dataset.col ?? "") : "",
  );
  if (keys.length === visible.length && visible.every((column, i) => column.key === keys[i])) return;
  row.innerHTML = visible.map((column) => `<td data-col="${column.key}"></td>`).join("");
}

function lastLinkedDetailRow(tr: Element): Element {
  let last = tr;
  while (
    last.nextElementSibling instanceof HTMLTableRowElement &&
    last.nextElementSibling.classList.contains("sfe-hist-linked-detail")
  ) {
    last = last.nextElementSibling;
  }
  return last;
}

/** Reconcile tbody against `filtered`, reusing rows by transaction id. Rows that
 *  survive a refresh keep their DOM nodes, so nothing below the table jumps. */
function patchHistoryTableBody(
  tbody: HTMLElement,
  filtered: HistoryTxn[],
  columns: ColumnState[],
  byId: Map<number, HistoryTxn>,
  all: HistoryTxn[],
  byContract: Map<string, HistoryTxn[]>,
): void {
  const visible = visibleTableColumns(columns);
  if (!filtered.length) {
    setContainerHtml(
      tbody,
      `<tr><td colspan="${Math.max(visible.length, 1)}" class="muted">No transactions match these filters.</td></tr>`,
    );
    return;
  }
  // Row-managed from here on, so any empty-state markup signature is void.
  renderedContainerHtml.delete(tbody);

  // Implied legs carry no id, so they are keyed by their order among themselves.
  let anonymousWanted = 0;
  const wantedKeys = filtered.map((txn) => {
    const id = Number(txn.id) || 0;
    return id ? String(id) : `0#${anonymousWanted++}`;
  });
  const wanted = new Set(wantedKeys);

  let anonymousRows = 0;
  const reusable = new Map<string, HTMLTableRowElement>();
  for (const child of [...tbody.children]) {
    const id = child instanceof HTMLTableRowElement ? (child.dataset.txnId ?? "") : "";
    // Detail rows are re-derived below; placeholders and dropped rows go now, so
    // the walk below never has to step over a row that is on its way out.
    if (!id || child.classList.contains("sfe-hist-linked-detail")) {
      child.remove();
      continue;
    }
    const key = id === "0" ? `0#${anonymousRows++}` : id;
    if (!wanted.has(key) || reusable.has(key)) {
      child.remove();
      continue;
    }
    reusable.set(key, child as HTMLTableRowElement);
  }

  const indexes = buildHistoryRowIndexes(all);
  let cursor: Element | null = tbody.firstElementChild;
  filtered.forEach((txn, index) => {
    const id = Number(txn.id) || 0;
    const key = wantedKeys[index]!;
    const expanded = id > 0 && expandedLinkedTxnIds.has(id);
    let row = reusable.get(key);
    if (row) {
      reusable.delete(key);
      if (row === cursor) cursor = row.nextElementSibling;
      else tbody.insertBefore(row, cursor);
      syncHistoryRowColumns(row, visible);
      patchHistoryParentRow(row, txn, visible, expanded, indexes);
    } else {
      row = document.createElement("tr");
      row.dataset.txnId = String(id);
      row.innerHTML = visible
        .map((column) => renderHistoryCell(column.key, txn, { linkedExpanded: expanded, ...indexes }))
        .join("");
      tbody.insertBefore(row, cursor);
    }
    if (expanded) {
      insertLinkedDetailRows(row, txn, visible, byId, all, byContract, indexes);
      cursor = lastLinkedDetailRow(row).nextElementSibling;
    }
  });

  for (const row of reusable.values()) row.remove();
}

function softUpdateHistoryPanel(
  panel: HTMLElement,
  state: HistoryFilterState,
  cache: HistoryPanelCache,
  ledgerSource: LedgerSource,
  stale = false,
): boolean {
  const root = panel.querySelector<HTMLElement>(".sfe-web-native-history");
  const tbody = panel.querySelector<HTMLElement>("#wheelHistoryBody");
  if (!root || !tbody) return false;
  // The mounted shell must belong to the ledger being painted.
  const wantView = ledgerSource === "own_ledger" ? "history-local" : "history";
  if (root.dataset.wheelView !== wantView) return false;

  sanitizeHistoryMonthFilter(state, ledgerSource);
  const filtered = sortTransactions(applyFilters(cache.transactions, state), ledgerSource);
  const tableId = historyTableId(ledgerSource);
  const columns = loadTableColumns(tableId, getTableColumnDefaults(tableId));
  const byId = buildTxnById(cache.transactions);
  const byContract = buildOptionSymbolIndex(cache.transactions);

  syncHistoryFilterBar(root, state, cache, ledgerSource);
  syncHistoryTableChrome(root, columns);
  patchHistoryTableBody(tbody, filtered, columns, byId, cache.transactions, byContract);

  const note = panel.querySelector<HTMLElement>("#wheelHistFilterNote");
  if (note) {
    const staleNote = stale ? " · showing cached data, refreshing…" : "";
    note.textContent = `${filtered.length.toLocaleString("en-US")} transaction${filtered.length === 1 ? "" : "s"}${staleNote}`;
  }

  syncFocusRowMenuAfterDomUpdate();
  return true;
}

function refreshHistoryPanelInBackground(
  panel: HTMLElement,
  state: HistoryFilterState,
  ledgerSource: LedgerSource,
  generation: number,
): void {
  markHistoryRefreshing(panel, true);
  void loadPanelCache(false, ledgerSource)
    .then((cache) => {
      if (mountedPanel !== panel || generation !== renderGeneration) return;
      mountPanel(panel, state, cache, false, ledgerSource, generation);
    })
    .catch(() => {
      /* keep showing cached rows — filter clicks stay client-side */
    })
    .finally(() => {
      if (mountedPanel === panel && generation === renderGeneration) {
        markHistoryRefreshing(panel, false);
      }
    });
}

function mountPanel(
  panel: HTMLElement,
  state: HistoryFilterState,
  cache: HistoryPanelCache,
  stale = false,
  ledgerSource: LedgerSource = "stockforge",
  _generation = renderGeneration,
): void {
  mountedLedgerSource = ledgerSource;
  liveHistoryCache = cache;
  // Filter listeners live as long as the mounted shell, so they must keep
  // pointing at one state object per ledger even across re-mounts.
  const activeState = canonicalFilterState(state, ledgerSource);
  liveHistoryFilterState = activeState;
  liveHistoryLedgerSource = ledgerSource;
  const historyHost = panel;

  const paint = (showStale: boolean): void => {
    const liveCache = liveHistoryCache ?? cache;
    repaintHistoryMarks = () => {
      if (mountedPanel?.isConnected) paint(false);
    };
    const liveState = liveHistoryFilterState ?? activeState;
    const liveSource = liveHistoryLedgerSource;
    sanitizeHistoryMonthFilter(liveState, liveSource);
    const filtered = sortTransactions(
      applyFilters(liveCache.transactions, liveState),
      liveSource,
    );
    const tableId = historyTableId(liveSource);
    const columns = loadTableColumns(tableId, getTableColumnDefaults(tableId));
    historyHost.innerHTML = renderPanelShell(
      liveState,
      liveCache,
      filtered,
      columns,
      showStale,
      liveSource,
    );
    // Bound once per shell — later updates patch the bar instead of rebuilding it.
    bindFilters(
      historyHost,
      liveState,
      rerender,
      liveSource,
      // Falls back to the last painted cache while a forced refetch is in flight.
      () => activeHistoryCache()?.transactions ?? liveHistoryCache?.transactions ?? [],
    );

    const resetBtn = historyHost.querySelector<HTMLElement>("#wheelHistResetFilters");
    if (resetBtn) {
      ensureColumnPickerBesideReset(resetBtn, {
        tableId,
        title: tableColumnTitle(tableId),
        defaults: getTableColumnDefaults(tableId),
        onApply: () => rerender(),
      });
    }
  };

  const softRefresh = (): void => {
    const liveCache = liveHistoryCache;
    const liveState = liveHistoryFilterState;
    if (!liveCache || !liveState) {
      paint(false);
      return;
    }
    if (
      !softUpdateHistoryPanel(
        historyHost,
        liveState,
        liveCache,
        liveHistoryLedgerSource,
        false,
      )
    ) {
      paint(false);
    }
    syncFocusRowMenuAfterDomUpdate();
  };

  // Filter, column and expand changes all morph the mounted shell in place;
  // a full repaint is the fallback for a shell that is not there yet.
  const rerender = (): void => {
    withPreservedInputFocus(() => softRefresh());
  };

  historyPanelRerender = rerender;
  historyRerenders.set(ledgerSource, rerender);
  bindHistoryTxnMenu();
  bindHistoryCashEdit();
  bindHistoryTxnNotes();

  if (panelHasDisplayedHistory(historyHost)) {
    if (softUpdateHistoryPanel(historyHost, activeState, cache, ledgerSource, stale)) {
      // Keep filter bindings / column picker from the existing shell.
      return;
    }
  }
  paint(stale);
}

async function loadPanelCache(force: boolean, source: LedgerSource = "stockforge"): Promise<HistoryPanelCache> {
  const isLocal = source === "own_ledger";
  const cacheRef = isLocal ? panelCacheLocal : panelCache;

  if (!force && cacheRef) {
    const cachedCash = isLocal ? countCachedCashRows(cacheRef.transactions) : 0;
    if (isLocal && cachedCash === 0) {
      force = true;
    } else if (Date.now() - cacheRef.fetchedAt < CACHE_MS) {
      return cacheRef;
    }
  }
  const inflightRef = isLocal ? panelInflightLocal : panelInflight;
  if (inflightRef && !force) return inflightRef;

  const load = async (): Promise<HistoryPanelCache> => {
    let transactions: HistoryTxn[];
    if (source === "own_ledger") {
      let bundle = await loadOwnLedgerHistoryBundle(force);
      if ((bundle.cashFlows?.length ?? 0) === 0) {
        try {
          const health = await loadOwnLedgerHealth(true);
          if (health.cashFlowCount > 0) {
            bundle = await loadOwnLedgerHistoryBundle(true);
          }
        } catch {
          /* health unavailable — keep empty cash list */
        }
      }
      transactions = (await loadOwnLedgerHistoryDisplayRows(
        async () => prepareOwnLedgerHistoryTransactions(bundle.transactions) as HistoryTxn[],
        async () => bundle.cashFlows,
      )) as HistoryTxn[];
    } else {
      const raw = await loadTransactionsSnapshot(wheelGet, force, source);
      transactions = prepareHistoryTransactions(raw) as HistoryTxn[];
    }
    const cache: HistoryPanelCache = {
      transactions,
      years: extractYears(transactions),
      fetchedAt: Date.now(),
    };
    if (isLocal) panelCacheLocal = cache;
    else panelCache = cache;
    return cache;
  };

  const inflight = load().finally(() => {
    if (isLocal) panelInflightLocal = null;
    else panelInflight = null;
  });
  if (isLocal) panelInflightLocal = inflight;
  else panelInflight = inflight;
  return inflight;
}

export async function renderNativeHistoryPanel(
  panel: HTMLElement,
  opts: { force?: boolean; ledgerSource?: LedgerSource } = {},
): Promise<void> {
  const generation = ++renderGeneration;
  mountedPanel = panel;
  void ensureLedgerMarkIndex().catch(() => null);
  const force = opts.force ?? false;
  const ledgerSource = opts.ledgerSource ?? "stockforge";
  mountedLedgerSource = ledgerSource;
  const state = loadFilters(ledgerSource);
  const panelCacheForSource = ledgerSource === "own_ledger" ? panelCacheLocal : panelCache;

  const hasCached = Boolean(panelCacheForSource?.transactions.length);
  const showsRows = panelHasDisplayedHistory(panel);
  if (!hasCached && !showsRows) {
    panel.innerHTML =
      ledgerSource === "own_ledger"
        ? `<div class="sfe-web-panel-loading">Loading transaction history (SF+ ledger)…</div>`
        : `<div class="sfe-web-panel-loading">Loading transaction history…</div>`;
  } else if (!hasCached || force) {
    // Rows already on screen stay put while the refetch runs.
    markHistoryRefreshing(panel, true);
  }

  try {
    if (hasCached && !force) {
      if (ledgerSource === "own_ledger" && (await ownLedgerCashCacheStale(panelCacheForSource))) {
        invalidateHistoryPanelCache("own_ledger");
        const cache = await loadPanelCache(true, ledgerSource);
        if (mountedPanel !== panel || generation !== renderGeneration) return;
        mountPanel(panel, state, cache, false, ledgerSource, generation);
        return;
      }
      mountPanel(panel, state, panelCacheForSource!, false, ledgerSource, generation);
      if (Date.now() - panelCacheForSource!.fetchedAt >= CACHE_MS) {
        refreshHistoryPanelInBackground(panel, state, ledgerSource, generation);
      }
      return;
    }

    if (panelCacheForSource?.transactions.length) {
      mountPanel(panel, state, panelCacheForSource, true, ledgerSource, generation);
    }

    const cache = await loadPanelCache(force, ledgerSource);
    if (mountedPanel !== panel || generation !== renderGeneration) return;
    mountPanel(panel, state, cache, false, ledgerSource, generation);
  } catch (err) {
    if (mountedPanel !== panel || generation !== renderGeneration) return;
    if (ledgerSource === "own_ledger") {
      paintHistoryLoadError(panel, err, ledgerSource);
      return;
    }
    paintPanelLoadError(panel, err, {
      title: "Transaction history",
      view: "history",
      fallback: "Could not load transaction history.",
    });
  } finally {
    if (mountedPanel === panel && generation === renderGeneration) {
      markHistoryRefreshing(panel, false);
    }
  }
}

export function invalidateHistoryPanelCache(source?: LedgerSource): void {
  if (!source || source === "stockforge") {
    panelCache = null;
    panelInflight = null;
  }
  if (!source || source === "own_ledger") {
    panelCacheLocal = null;
    panelInflightLocal = null;
  }
  expandedLinkedTxnIds.clear();
  invalidateTransactionsSnapshot(source);
}
